import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  MenuItem
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { client } from "@frontend/features/common/services";
import { UpdateGupshupSubscriptionDto } from "@bandido/shared/dtos";
import { ControlledTextField } from "@frontend/features/common/components/ControlledTextField";
import { GupshupModes } from "@bandido/shared/types";
import { useQueryClient } from "react-query";
import { QueryKeys } from "@frontend/features/common/types";
import { useSnackBar } from "@frontend/features/common/components/SnackBarContext";

interface Props {
  open: boolean;
  agencyId: number;
  onClose: () => void;
}


export const GupshupCreateSubscription = ({ open, onClose, agencyId }: Props) => {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();

  const { handleSubmit, control, reset } = useForm<UpdateGupshupSubscriptionDto>({
    defaultValues: {
      tag: "random name",
      url: `/v1/api/whatsapp/inbound-message/${agencyId}`,
      showOnUI: true,
      version: 3,
      modes: []
    }
  });

  const onSubmit = async (values: UpdateGupshupSubscriptionDto) => {
    try {
      await client.post(`/whatsapp/gupshup/subscriptions/${agencyId}`, values);
      queryClient.invalidateQueries([QueryKeys.gupshupSubscriptions]);
      onClose();
      reset();
    } catch (e) {
      addToast({
        message: t('agencies.settings.whatsappSubscriptionError'),
        color: 'error',
      });
      console.log(e)
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>{t('whatsapp.settings.createNewSubscription')}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ControlledTextField
            control={control}
            name="tag"
            label={t('whatsapp.settings.tag')}
            placeholder={t('common.form.onlyLetters')}
            required
            errorMessage={t('common.form.required')}
            fullWidth
          />

          <ControlledTextField
            control={control}
            name="url"
            label={t('whatsapp.settings.url')}
            required
            errorMessage={t('common.form.required')}
            fullWidth
          />

          <ControlledTextField
            control={control}
            name="version"
            label={t('whatsapp.settings.version')}
            type="number"
            required
            errorMessage={t('common.form.required')}
            fullWidth
          />

          <Controller
            name="showOnUI"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={t('whatsapp.settings.showOnUI')}
              />
            )}
          />

          <Controller
            name="modes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label={t('whatsapp.settings.modes')}
                fullWidth
                SelectProps={{ multiple: true }}
                margin="normal"
              >
                {GupshupModes.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {mode}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained">{t('common.save')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
